/**
 * User Activity API Service - admin activity report + per-FP engagement.
 *
 * Backed by /api/admin/activity/* (system_admin gated) and
 * /api/inspector/{fp}/engagement (staff-only).
 */

import { apiClient } from './api'
import { downloadBlob } from '../utils/download'

export type ActivityWindow = '24h' | '7d' | '30d'

/** One home for window display labels (cards, filter, timeline drawer). */
export const WINDOW_LABELS: Record<ActivityWindow, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
}

export interface WindowSummary {
  window: ActivityWindow
  viewers_by_role: Record<string, number>
  viewers_total: number
  actors_by_role: Record<string, number>
  actors_total: number
}

export interface ActivitySummaryResponse {
  windows: WindowSummary[]
  generated_at: string
}

export interface ActivityUserRow {
  user_id: number
  email: string
  name: string
  role: string
  is_active: boolean
  last_seen_at?: string | null
  window_last_seen_at?: string | null
  event_count: number
  action_count: number
  last_action_at?: string | null
}

export interface ActivityUsersResponse {
  window: string
  total: number
  users: ActivityUserRow[]
}

export interface ActivityUsersFilters {
  window?: ActivityWindow
  role?: string
  market?: string
  fp?: string
  search?: string
}

export interface TimelineItem {
  kind: 'view' | 'action'
  at: string
  label: string
  surface?: string | null
  fp_code?: string | null
  event_count?: number | null
  event_type?: string | null
}

export interface ActivityTimelineResponse {
  user_id: number
  email: string
  name: string
  role: string
  window: string
  items: TimelineItem[]
}

export interface EngagementUser {
  user_id: number
  name: string
  email: string
  role: string
  is_active: boolean
  has_fp_access: boolean
  last_active_on_fp?: string | null
  event_count: number
}

export interface FpEngagementResponse {
  fp_code: string
  users: EngagementUser[]
}

const buildQuery = (filters: ActivityUsersFilters): string => {
  const qs = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => Boolean(v)) as [string, string][]
  ).toString()
  return qs ? `?${qs}` : ''
}

class ActivityApi {
  async getSummary(): Promise<ActivitySummaryResponse> {
    const response = await apiClient.get('/admin/activity/summary')
    return response.data
  }

  async getUsers(filters: ActivityUsersFilters = {}): Promise<ActivityUsersResponse> {
    const response = await apiClient.get(`/admin/activity/users${buildQuery(filters)}`)
    return response.data
  }

  async getTimeline(
    userId: number,
    window: ActivityWindow = '7d'
  ): Promise<ActivityTimelineResponse> {
    const response = await apiClient.get(
      `/admin/activity/users/${userId}/timeline${buildQuery({ window })}`
    )
    return response.data
  }

  async exportExcel(filters: ActivityUsersFilters = {}): Promise<void> {
    await downloadBlob(`/admin/activity/export${buildQuery(filters)}`, 'user_activity')
  }

  async getFpEngagement(fpCode: string): Promise<FpEngagementResponse> {
    const response = await apiClient.get(`/inspector/${fpCode}/engagement`)
    return response.data
  }
}

export const activityApi = new ActivityApi()
